import React from 'react';
import { Clock, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';

interface DeliveryCountdownBadgeProps {
  deadline?: string | null;
  status?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md';
}

export function DeliveryCountdownBadge({
  deadline,
  status,
  className = '',
  size = 'sm',
}: DeliveryCountdownBadgeProps) {
  if (!deadline) return null;

  const isCompleted = status === 'completed' || status === 'delivered' || status === 'done';
  if (isCompleted) {
    return (
      <span
        className={`inline-flex items-center rounded-full font-medium border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800/40 ${
          size === 'xs' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-0.5 gap-1.5'
        } ${className}`}
      >
        <CheckCircle className="w-3 h-3" />
        <span>Delivered</span>
      </span>
    );
  }

  // Calculate day difference safely across all timezones
  const trimmedDeadline = typeof deadline === 'string' ? deadline.trim() : '';
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  let targetMidnight: number;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDeadline)) {
    const [y, m, d] = trimmedDeadline.split('-').map(Number);
    targetMidnight = new Date(y, m - 1, d).getTime();
  } else {
    const targetDate = new Date(deadline);
    if (isNaN(targetDate.getTime())) return null;
    targetMidnight = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()).getTime();
  }

  const diffDays = Math.round((targetMidnight - todayMidnight) / (1000 * 60 * 60 * 24));

  let label = '';
  let badgeClasses = '';
  let icon: React.ReactNode = <Clock className="w-3 h-3" />;

  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    label = `Overdue by ${absDays}d`;
    badgeClasses = 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900/60 font-semibold animate-pulse';
    icon = <AlertCircle className="w-3 h-3 text-rose-600 dark:text-rose-400" />;
  } else if (diffDays === 0) {
    label = 'Due today';
    badgeClasses = 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/60 font-semibold';
    icon = <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400" />;
  } else if (diffDays === 1) {
    label = 'Due tomorrow';
    badgeClasses = 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50 font-medium';
    icon = <Clock className="w-3 h-3 text-amber-600 dark:text-amber-400" />;
  } else if (diffDays <= 4) {
    label = `${diffDays} days left`;
    badgeClasses = 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50 font-medium';
    icon = <Clock className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />;
  } else {
    label = `${diffDays} days left`;
    badgeClasses = 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700 font-medium';
    icon = <Clock className="w-3 h-3 text-slate-500 dark:text-slate-400" />;
  }

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-1',
    sm: 'text-xs px-2 py-0.5 gap-1.5',
    md: 'text-xs px-2.5 py-1 gap-1.5',
  }[size];

  return (
    <span
      className={`inline-flex items-center rounded-full border shadow-2xs whitespace-nowrap transition-colors ${badgeClasses} ${sizeClasses} ${className}`}
      title={`Deadline: ${deadline} (${label})`}
    >
      {icon}
      <span>{label}</span>
    </span>
  );
}
